import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { css } from "@emotion/css";

import IconsTablet from "../img/svg/header-icons-tablet.svg";
import IconsMobile from "../img/svg/header-icons-mobile.svg";

import Logo from "../img/svg/logo.svg";
import LogoM from "../img/svg/header-mobile-logo.svg";
import LogoBottom from "../img/svg/header-bottom-logo.svg";

import BG from "../img/svg/header-bg.svg";
import MenuIcon from "../img/svg/menu-icon.svg";
import CloseIcon from "../img/svg/close-button.svg";
import IGIcon from "../img/svg/ig-icon.svg";
import FBIcon from "../img/svg/fb-icon.svg";
import TTIcon from "../img/svg/tt-icon.svg";
import { laptop, tablet } from "../styles/breakpoints";
import Clickable from "./Clickable";
import { useIsMobile } from "./helpers/ScreenCheck";
import { useI18n } from "../i18n";
import LanguageSwitcher from "./LanguageSwitcher";

const Header = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const isMobile = useIsMobile(744);
  const { t } = useI18n();

  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [pathname]);

  const style = {
    container: css({
      position: "absolute",
      left: 0,
      right: 0,
      top: 0,
      margin: "auto",
      maxWidth: 1830,
      paddingTop: 14,
      display: "flex",
      flexDirection: "row",
      flexWrap: "nowrap",
      alignContent: "flex-start",
      justifyContent: "space-between",
      alignItems: "flex-start",
      width: "100%",
      zIndex: 10,

      a: {
        color: "#FFF",
        fontFamily: "Syne",
        fontSize: 16,
        fontStyle: "normal",
        fontWeight: 600,
        lineHeight: "normal",
        letterSpacing: 0.32,
        textTransform: "uppercase",

        "&:hover": {
          textDecoration: 'underline'
        }
      },
      ".wrapper": {
        width: "100%",
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        alignContent: "center",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 25.714,
        paddingLeft: 26,
        paddingRight: 26,
        paddingTop: 22,

        ".group": {
          borderRadius: 85.714,
          background:
            "linear-gradient(90deg, rgba(123, 122, 193, 0.50) 0%, rgba(190, 150, 255, 0.50) 100%)",
          backdropFilter: "blur(6.857142925262451px)",
          display: "flex",
          padding: "17.143px 25.714px",
          justifyContent: "center",
          alignItems: "center",
          gap: 25.714,
        },
        ".logo": {
          marginRight: "auto",
        },
      },
      ".copy": {
        color: "#000",
        textAlign: "center",
        fontFamily: "Inter",
        fontSize: 14,
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
        letterSpacing: 0.14,
        opacity: 0.7,
        marginBottom: 38,
        zIndex: 1,
      },

      ".social": {
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        alignContent: "center",
        justifyContent: "center",
        alignItems: "center",
        gap: 30,
        padding: 30,
        zIndex: 1,

        img: {
          width: 88,
          height: 88,
        },
      },
      ".menu": {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "1fr 1fr",
        gap: 48,
        justifyItems: "start",
        margin: "0px 40px",
        padding: "39px 0px",
        borderBottomColor: "rgba(255, 255, 255, 0.30)",
        borderBottomWidth: 1.375,
        borderBottomStyle: "solid",
        zIndex: 1,

        a: {
          color: "#FFF",
          fontFamily: "Syne",
          fontSize: 46.5,
          fontStyle: "normal",
          fontWeight: 600,
          lineHeight: "normal",
          textTransform: "capitalize",
        },

        hr: {
          display: "none",
        },
      },
      ".top": {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        alignContent: 'center',
        padding: '30px 20px',
      },
      ".policy": {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "1fr",
        gap: 48,
        justifyItems: "start",
        margin: "0px 40px",
        padding: "39px 0px",
        marginRight: "auto",
        zIndex: 1,

        a: {
          color: "#FFF",
          fontFamily: "Syne",
          fontSize: 28,
          fontStyle: "normal",
          fontWeight: 600,
          lineHeight: "normal",
          textTransform: "capitalize",
          maxWidth: 180,
        },
      },

      ".logo-mobile, .menu-button": {
        zIndex: 999,
        position: "relative",
        display: "block",
      },
      ".menu-button": {
        img: {
          width: 81.5,
          height: 43,
        },
      },
      ".logo-mobile": {
        width: 130,
        height: 43,
      },

      ".logo-bottom, .icons": {
        maxWidth: 662.303,
        width: "100%",
        height: "auto",
        margin: "auto",
        marginBottom: 30,
        zIndex: 1,
      },
      ".bg": {
        backgroundImage: `url(${BG})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "top center",
        backgroundSize: "auto",
        position: "absolute",
        left: 0,
        top: 0,
        zIndex: 0,
        width: "100%",
        height: "100%",
      },
      ".nav-mobile": {
        transition: "all 1s ease",
        width: "100%",
        zIndex: 9999,
        position: "fixed",
        left: -1200,
        top: 0,
        display: "flex",
        flexDirection: "column",
        flexWrap: "nowrap",
        alignContent: "center",
        justifyContent: "flex-start",
        alignItems: "stretch",
        height: "100%",
        overflow: "auto",
        gap: 0,
        backgroundColor: "#ffd7f8",

        "&.active": {
          left: 0,
        },
      },

      ".mobile": {
        display: "none",
      },
      [laptop]: {
        position: 'fixed',
        top: 0,
        left: 0,
        ".desktop": {
          display: "none",
        },

        ".mobile": {
          display: "flex",
        },
      },
      [tablet]: {
        ".top": {
          paddingBottom: '10px !important'
        },
        ".nav-mobile": {
          backgroundColor: "#ffe5f0",
        },
        ".bg": {
          backgroundSize: 800,
        },
        ".wrapper": {
          paddingLeft: 20,
          paddingRight: 20,
        },
        ".menu-button": {
          img: {
            width: 55,
            height: 29,
          },
        },
        ".logo-mobile": {
          width: 85.5,
          height: 29,
        },

        ".logo-bottom, .icons": {
          margin: "0px 20px",
          width: "auto",
        },
        ".policy": {
          margin: "0px 20px",
          padding: "20px 0px",
          a: {
            fontSize: 20,
          },
        },
        ".menu": {
          gap: 20,
          gridTemplateColumns: "1fr",
          gridTemplateRows: "1fr",
          margin: "0px 20px",
          padding: "20px 0px",

          a: {
            fontSize: 42,
          },
        },

        ".social": {
          gap: 31,
          padding: 20,

          img: {
            width: 70,
            height: 70,
          },
        },

        ".copy": {
          fontSize: 14,
          lineHeight: "18px",
          marginBottom: 20,
          marginTop: 40,
        },
      },
    }),
  };

  return (
    <header className={style.container}>
      <div className="wrapper desktop">
        <div style={{ display: "flex", flexDirection: "row", gap: 12 }}>
          <div className="group">
            <img src={Logo} width="129.137" height="20.327" alt={t("header_logo_alt")} />
          </div>

          <div className="group">
            <Link to="/">{t("header_home")}</Link>
            {/* TEMPORARILY DISABLED: "How it works" link. Re-enable by uncommenting. */}
            {/* <Link to="/technology">{t("header_how")}</Link> */}
            <Link to="/retailers">{t("header_retailers")}</Link>
            <Link to="/about">{t("header_about")}</Link>
            <Link to="/blog">{t("header_blog")}</Link>
          </div>
        </div>

        <div className="group">
          <LanguageSwitcher />
          <Link to="/contact">{t("header_contact")}</Link>
          <a href="https://app.zilouet.ai/login">{t("header_sign_in")}</a>
          <a href="https://app.zilouet.ai/signup">{t("header_sign_up")}</a>
        </div>
      </div>

      <div className="wrapper mobile">
        <img src={LogoM} alt={t("header_logo_alt")} className="logo-mobile" />
        <LanguageSwitcher variant="topbar" />

        <Clickable onClick={() => setOpen(!open)} className="menu-button">
          <img src={MenuIcon} alt={t("header_menu_open")} />
        </Clickable>

        <div className={`nav-mobile${open ? " active" : ""}`}>
          <div className="top">
            <img src={LogoM} alt={t("header_logo_alt")} className="logo-mobile" />
            <LanguageSwitcher variant="mobile" />
            <Clickable onClick={() => setOpen(!open)} className="menu-button">
              <img src={CloseIcon} alt={t("header_menu_open")} />
            </Clickable>
          </div>
          <span className="bg"></span>
          <div className="menu">
            <Link to="/">{t("header_home")}</Link>
            {/* TEMPORARILY DISABLED: "How it works" link. Re-enable by uncommenting. */}
            {/* <Link to="/technology">{t("header_how")}</Link> */}
            <Link to="/retailers">{t("header_retailers")}</Link>
            <Link to="/about">{t("header_about")}</Link>
            <Link to="/blog">{t("header_blog")}</Link>
            <Link to="/contact">{t("header_contact")}</Link>
            <a href="https://app.zilouet.ai/login">{t("header_sign_in")}</a>
            <a href="https://app.zilouet.ai/signup">{t("header_sign_up")}</a>
          </div>

          <div className="policy">
            <Link to="/privacy-policy">{t("header_privacy")}</Link>
            <Link to="/terms-conditions">{t("header_terms")}</Link>
          </div>
          <div className={`social`}>
            <a href="https://www.tiktok.com/@zilouet?_t=ZN-8zmTxICI9Ru&_r=1" target="_blank" rel="noopener noreferrer">
              <img src={TTIcon} alt={t("social_alt_tiktok")} />
            </a>
            <a href="https://www.instagram.com/zilouetofficial?igsh=dmI0aGltN241bDZw" target="_blank" rel="noopener noreferrer">
              <img src={IGIcon} alt={t("social_alt_instagram")} />
            </a>
            <a href="https://www.facebook.com/zilouet" target="_blank" rel="noopener noreferrer">
              <img src={FBIcon} alt={t("social_alt_facebook")} />
            </a>
          </div>
          <img
            src={isMobile ? IconsMobile : IconsTablet}
            alt={t("social_alt_icons")}
            className="icons"
          />
          <img src={LogoBottom} alt={t("header_logo_alt")} className="logo-bottom" />
          <div className="copy">©2024 Zilouet. All rights reserved</div>
        </div>
      </div>
    </header>
  );
};

export default Header;
